import { Button, IconButton, Slider, Stack, Typography, useTheme } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';

export const AudioPlayer = ({
  audioUrl,
  onTimeUpdate,
  onPlayingChange,
}: {
  audioUrl: string;
  onTimeUpdate?: (currentTimeSeconds: number) => void;
  onPlayingChange?: (isPlaying: boolean) => void;
}) => {
  const theme = useTheme();
  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);

  const playbackSpeeds = [1.0, 1.5, 2.0];

  // Observe container width
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (audio?.paused) {
      void audio?.play();
    } else {
      audio?.pause();
    }
  };

  const handlePlayingChange = (playing: boolean) => {
    setIsPlaying(playing);
    onPlayingChange?.(playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
    onTimeUpdate?.(audioRef.current.currentTime);
  };

  const handleDurationChange = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current?.duration);
  };

  const handleTimestampChange = (_: Event, newValue: number | number[]) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = newValue as number;
    setCurrentTime(newValue as number);
    onTimeUpdate?.(newValue as number);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const cyclePlaybackSpeed = () => {
    if (!audioRef.current) return;
    const currentIndex = playbackSpeeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % playbackSpeeds.length;
    const newRate = playbackSpeeds[nextIndex];
    audioRef.current.playbackRate = newRate;
    setPlaybackRate(newRate);
  };

  // Dynamically hide pieces of audio player based on container width
  const showSlider = containerWidth > 270;
  const showFullTime = containerWidth > 200;
  const showSpeedControl = containerWidth > 165;

  return (
    <Stack
      ref={containerRef}
      direction="row"
      alignItems="center"
      my={theme.spacing(1)}
      width="100%"
    >
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleDurationChange}
        onPlay={() => handlePlayingChange(true)}
        onPause={() => handlePlayingChange(false)}
        onEnded={() => handlePlayingChange(false)}
      />

      <IconButton onClick={togglePlayPause} disableRipple sx={{ p: 0 }}>
        <DynamicIcon
          filled
          name={isPlaying && currentTime !== duration ? 'pause_circle' : 'play_circle'}
          color={theme.palette.primary.main}
          sx={{ fontSize: 40 }}
          mr={theme.spacing(4)}
        />
      </IconButton>

      <Stack direction="row" alignItems="center" flex={1} spacing={2}>
        {showSlider ? (
          <Slider
            value={currentTime}
            min={0}
            max={duration}
            step={0.1}
            onChange={handleTimestampChange}
            size="small"
            sx={{
              minWidth: 0,
              flexGrow: 1,
              '& .MuiSlider-thumb': {
                color: theme.palette.primary.main,
              },
              '& .MuiSlider-track': {
                color: theme.palette.primary.main,
              },
            }}
          />
        ) : null}

        <Typography variant="body2" textAlign="center" sx={{ whiteSpace: 'nowrap' }}>
          {formatTime(currentTime)}
          {showFullTime ? ` / ${formatTime(duration)}` : null}
        </Typography>
      </Stack>

      {showSpeedControl ? (
        <Button
          variant="text"
          size="small"
          onClick={cyclePlaybackSpeed}
          color="inherit"
          sx={{
            background: theme.palette.custom.OV.sys.surfaceContainerHigh,
            borderRadius: 100,
            minWidth: 0,
            width: '3rem',
            textTransform: 'lowercase',
            marginLeft: theme.spacing(1),
          }}
        >
          {playbackRate}x
        </Button>
      ) : null}
    </Stack>
  );
};
